import './App.scss';
import React, { useEffect } from 'react';
import Cookie from 'js-cookie';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import AppRouter from '@/Router/Router';
import initDatadogLogs from '@/datadog/logs';
import StateProvider from '@/StateProvider';
import isDevEnv from '@/utils/isDevEnv';
import { StoreProvider } from '@/hooks/useStore';
import DevTools from '@/DevTools';
import EventCollectorContextProvider from '@/hooks/context/EventCollector/EventCollectorContextProvider';
import { bootstrapKeycloakStore } from '@/hooks/zustandStore';

const queryClient = new QueryClient();

const isRecaptchaEnabled = import.meta.env.VITE_FEATURE_RECAPTCHA === 'true';
const recaptchaKey = import.meta.env.VITE_RECAPTCHA_KEY;

export default function App() {
  initDatadogLogs();

  useEffect(() => {
    const handleOnLoadEvent = () => {
      const cookieToken = Cookie.get('token');
      const cookieCrossLoginToken = Cookie.get('crossLoginToken');

      if (Boolean(cookieToken) && !sessionStorage.getItem('token')) {
        sessionStorage.setItem('token', cookieToken || '');
      }

      if (
        Boolean(cookieCrossLoginToken) &&
        !sessionStorage.getItem('crossLoginToken')
      ) {
        sessionStorage.setItem('crossLoginToken', cookieCrossLoginToken || '');
      }
      Cookie.remove('token');
      Cookie.remove('crossLoginToken');
    };

    bootstrapKeycloakStore();

    window.addEventListener('load', handleOnLoadEvent);

    return () => {
      window.removeEventListener('load', handleOnLoadEvent);
    };
  }, []);

  return (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools
          initialIsOpen={false}
          buttonPosition="bottom-left"
        />
        <StoreProvider>
          <EventCollectorContextProvider>
            {isDevEnv() && <DevTools />}
            <StateProvider>
              {isRecaptchaEnabled ? (
                <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey}>
                  <AppRouter />
                </GoogleReCaptchaProvider>
              ) : (
                <AppRouter />
              )}
            </StateProvider>
          </EventCollectorContextProvider>
        </StoreProvider>
      </QueryClientProvider>
    </React.StrictMode>
  );
}
